import {
  GROUND_ROW,
  LEVEL_ROWS,
  TILE,
} from "../../constants";

export { GROUND_ROW, LEVEL_ROWS, TILE };

export type LevelTheme = "overworld" | "underground" | "athletic" | "castle";

export type WarpDest = {
  levelId: string;
  tx?: number;
  ty?: number;
  enter?: "pipe-down" | "pipe-up";
};

export type EntitySpawn =
  | { kind: "walker"; tx: number; ty: number }
  | { kind: "troopa"; tx: number; ty: number; color: "green" | "red" }
  | { kind: "plant"; tx: number }
  | { kind: "podoboo"; tx: number }
  | { kind: "firebar"; tx: number; ty: number; length: number; speed: number }
  | { kind: "platform"; tx: number; ty: number; tiles: number; axis: "x" | "y"; span: number }
  | { kind: "scale"; left: number; right: number; ty: number; tiles: number }
  | { kind: "boss"; tx: number; ty: number }
  | { kind: "spring"; tx: number; ty: number };

export interface LevelDef {
  id: string;
  worldLabel: string;
  theme: LevelTheme;
  time: number;
  tiles: string[];
  entities: EntitySpawn[];
  pipes: { tx: number; height: number; dest?: WarpDest }[];
  playerSpawn: { tx: number; ty: number };
  flagX?: number;
  exitX?: number;
  axe?: { tx: number; ty: number };
  castleX?: number;
  next?: string;
  returnTo?: WarpDest;
}

export function makeGrid(width: number, fillGround = true): string[][] {
  return Array.from({ length: LEVEL_ROWS }, (_, y) =>
    Array.from({ length: width }, () => (fillGround && y >= GROUND_ROW ? "=" : ".")),
  );
}

export function setCell(grid: string[][], tx: number, ty: number, ch: string): void {
  const row = grid[ty];
  if (!row || tx < 0 || tx >= row.length || ty < 0 || ty >= grid.length) return;
  row[tx] = ch;
}

export function fillRect(
  grid: string[][],
  tx: number,
  ty: number,
  width: number,
  height: number,
  ch: string,
): void {
  for (let y = ty; y < ty + height; y += 1) {
    for (let x = tx; x < tx + width; x += 1) {
      setCell(grid, x, y, ch);
    }
  }
}

export function cutPit(grid: string[][], tx: number, width: number, lava = false): void {
  for (let x = tx; x < tx + width; x += 1) {
    setCell(grid, x, GROUND_ROW, lava ? "L" : ".");
    setCell(grid, x, GROUND_ROW + 1, lava ? "L" : ".");
  }
}

export function stampPipe(grid: string[][], tx: number, height: number): void {
  const top = GROUND_ROW - height;
  setCell(grid, tx, top, "T");
  setCell(grid, tx + 1, top, "U");
  for (let y = top + 1; y < GROUND_ROW; y += 1) {
    setCell(grid, tx, y, "P");
    setCell(grid, tx + 1, y, "Q");
  }
}

export function freeze(grid: string[][]): string[] {
  return grid.map((row) => row.join(""));
}

export function tileToWorld(tx: number, ty: number): { x: number; y: number } {
  return { x: tx * TILE, y: ty * TILE };
}

export function parseRows(tiles: string[]): { width: number; height: number } {
  const height = tiles.length;
  const width = tiles[0]?.length ?? 0;
  return { width, height };
}

export const SOLID_TILES = new Set(["=", "#", "?", "!", "K", "S", "T", "U", "P", "Q", "H", "B", "X", "-"]);
export const INTERACTIVE_TILES = new Set(["#", "?", "!", "I", "1", "R"]);
export const DECO_TILES = new Set(["C", "N", "O", "M", "F", "G", "A", "D", "L", "W"]);
export const LAVA_TILES = new Set(["L"]);
export const HAZARD_TILES = new Set(["L"]);

export function isSolidChar(ch: string): boolean {
  return SOLID_TILES.has(ch);
}

export function isInteractiveChar(ch: string): boolean {
  return INTERACTIVE_TILES.has(ch);
}
